import { RecordStatusBadge } from "@/components/user-record/record-status-badge";
import { recordAccountStatus } from "@/components/user-record/record-account-status";
import { formatRecordJoinedLabel } from "@/components/user-record/format-record-joined-label";
import { UserNameColumn } from "@/components/users/user-name-column";
import type { User } from "@/lib/api/user";
import { getRecordDisplayName } from "@/lib/redacted-display";
import { cn } from "@/lib/utils";
import { formatRoleLabel } from "@/lib/utils/role-label";
import { getPrimaryRole } from "@/lib/utils/system-roles";
import { Skeleton } from "@somethings-ui/web";
import { Link } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

export type UsersSort = "name" | "joined";
export type UsersSortDirection = "asc" | "desc";

const COLUMNS = [
  { id: "name", label: "Name", width: "minmax(240px,1fr)", sort: "name" },
  { id: "role", label: "Role", width: "140px", sort: null },
  { id: "groups", label: "Groups", width: "180px", sort: null },
  { id: "joined", label: "Join Date", width: "168px", sort: "joined" },
  { id: "status", label: "Status", width: "132px", sort: null },
] as const;

type UsersColumn = (typeof COLUMNS)[number]["id"];

const TEMPLATE = COLUMNS.map((column) => column.width).join(" ");

export function UsersTable({
  users,
  isPending,
  isRefreshing,
  sort,
  direction,
  onSort,
}: {
  users: User[];
  isPending: boolean;
  isRefreshing: boolean;
  sort: UsersSort | null;
  direction: UsersSortDirection | null;
  onSort: (sort: UsersSort) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-buttonBorder-tertiary bg-surfaces-z0">
      <div
        className={cn("grid", isRefreshing && "opacity-60 transition-opacity")}
        style={{ gridTemplateColumns: TEMPLATE }}
      >
        {COLUMNS.map((column) =>
          column.sort ? (
            <button
              key={column.id}
              type="button"
              onClick={() => column.sort && onSort(column.sort)}
              aria-sort={
                sort === column.sort
                  ? direction === "asc"
                    ? "ascending"
                    : "descending"
                  : "none"
              }
              className="flex h-11 min-w-0 items-center gap-xs bg-surfaces-z3 px-lg text-left font-body text-body-2 text-text-secondary"
            >
              {column.label}
              <SortIcon active={sort === column.sort} direction={direction} />
            </button>
          ) : (
            <div
              key={column.id}
              className="flex h-11 min-w-0 items-center bg-surfaces-z3 px-lg font-body text-body-2 text-text-secondary"
            >
              {column.label}
            </div>
          ),
        )}
        {isPending ? (
          <div className="col-span-full flex flex-col gap-md px-lg py-lg">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-2xl w-full" />
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="col-span-full flex h-24 items-center justify-center font-body text-body-2 text-text-secondary">
            No users
          </div>
        ) : (
          users.map((user) => <UserRow key={user.id} user={user} />)
        )}
      </div>
    </div>
  );
}

function UserRow({ user }: { user: User }) {
  return (
    <Link
      to="/users/$userId"
      params={{ userId: user.id }}
      className="col-span-full grid grid-cols-subgrid border-t border-buttonBorder-tertiary text-left hover:bg-surfaces-z2"
    >
      {COLUMNS.map((column) => (
        <span
          key={column.id}
          className="flex min-h-3xl min-w-0 items-center px-lg py-md"
        >
          <Cell user={user} column={column.id} />
        </span>
      ))}
    </Link>
  );
}

function Cell({ user, column }: { user: User; column: UsersColumn }) {
  switch (column) {
    case "name":
      return (
        <UserNameColumn
          profilePhoto={user.profile_picture_url ?? null}
          name={getRecordDisplayName(user)}
          researchConsent={
            user.profile?.metadata?.researchConsent?.consent === true
          }
          redactedFields={user.redacted_fields}
          userId={user.id}
        />
      );
    case "role":
      return (
        <RoleTag
          role={getPrimaryRole(user.systemRoles) ?? user.systemRoles?.[0]}
        />
      );
    case "groups": {
      const label = user.assigned_groups?.map((group) => group.name).join(", ");
      return (
        <span
          className={cn(
            "truncate font-body text-body-2",
            label ? "text-text-primary" : "text-text-tertiary",
          )}
        >
          {label || "No group"}
        </span>
      );
    }
    case "joined":
      return (
        <span className="font-body text-body-2 text-text-primary">
          {formatRecordJoinedLabel(user.created_at) ?? "—"}
        </span>
      );
    case "status":
      return <RecordStatusBadge status={recordAccountStatus(user)} />;
    default: {
      const exhaustive: never = column;
      return exhaustive;
    }
  }
}

function SortIcon({
  active,
  direction,
}: {
  active: boolean;
  direction: UsersSortDirection | null;
}) {
  if (!active || direction == null) {
    return (
      <ArrowUpDown className="size-3.5 text-text-placeholder" aria-hidden />
    );
  }

  const Icon = direction === "asc" ? ArrowUp : ArrowDown;
  return <Icon className="size-3.5 text-icon-primary" aria-hidden />;
}

function RoleTag({ role }: { role: string | null | undefined }) {
  const label = formatRoleLabel(role);
  if (!label) return null;
  return (
    <span className="inline-flex h-xl items-center rounded-full border border-buttonBorder-tertiary bg-surfaces-z0 px-2.5 font-body text-xs font-medium leading-none text-text-secondary">
      {label}
    </span>
  );
}
